import { useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { WorkflowStage } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, Card, CardContent, Input, ListSkeleton, useToast } from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { useSetOnboardingStep } from '../lib/onboarding/use-onboarding.js';
import { useStageConfiguration, useUpdateStageSettings } from '../lib/workflow/use-kanban.js';

/** Mirrors the API's `MAX_STAGE_LABEL_LENGTH`: a name has to fit on a board column. */
const MAX_LABEL_LENGTH = 32;

interface DraftStage {
  key: WorkflowStage;
  enabled: boolean;
  label: string;
}

/**
 * The shop's workflow: which stages it uses and what it calls them.
 *
 * The canonical order is not editable here, for the reason the garment-workflow screen gives —
 * the stage engine and every per-stage report are written against it. What a shop owns is the
 * switch and the name.
 *
 * Saving ticks the "Your workflow" checklist step. Every shop is born with the default stages,
 * so nothing in the data can show that the owner looked at them; pressing Save here does.
 */
export function WorkflowSettingsPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();
  const configuration = useStageConfiguration();
  const save = useUpdateStageSettings();
  const setStep = useSetOnboardingStep();

  // `settings.stages.write` is the owner's alone on the API; so it is here.
  const canWrite = role === 'owner';

  const [draft, setDraft] = useState<DraftStage[] | null>(null);

  if (configuration.isPending) {
    return (
      <>
        <PageHeader title={t('settings.workflow.title')} />
        <ListSkeleton rows={6} />
      </>
    );
  }

  const stages = configuration.data?.stages ?? [];
  const rows: DraftStage[] =
    draft ??
    stages.map((stage) => ({
      key: stage.key,
      enabled: stage.enabled,
      label: stage.renamed ? stage.label : '',
    }));

  const change = (key: WorkflowStage, patch: Partial<DraftStage>): void =>
    setDraft(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      try {
        await save.mutateAsync(
          rows.map((row) => ({
            key: row.key,
            enabled: row.enabled,
            label: row.label.trim() || null,
          })),
        );
        await setStep.mutateAsync({ key: 'stages', status: 'completed' });
        setDraft(null);
        toast({ title: t('settings.workflow.saved'), variant: 'success' });
      } catch (caught) {
        toast({
          title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <>
      <PageHeader
        title={t('settings.workflow.title')}
        description={t('settings.workflow.description')}
      />

      <Card>
        <CardContent className="pt-6">
          <form className="flex flex-col gap-4" onSubmit={submit}>
            <p className="text-sm text-muted-foreground">{t('settings.workflow.hint')}</p>

            <ul className="flex flex-col gap-2">
              {stages.map((stage) => {
                const row = rows.find((candidate) => candidate.key === stage.key);
                if (!row) return null;
                const defaultName = t(`stage.${stage.key}`);
                return (
                  <li
                    key={stage.key}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3"
                  >
                    <label className="flex min-w-40 items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        aria-label={t('settings.workflow.enabled', { stage: defaultName })}
                        checked={stage.required || row.enabled}
                        disabled={!canWrite || stage.required}
                        onChange={(event) => change(stage.key, { enabled: event.target.checked })}
                      />
                      {defaultName}
                    </label>
                    {stage.required ? (
                      <Badge variant="secondary">{t('settings.workflow.required')}</Badge>
                    ) : null}
                    <Input
                      className="ml-auto w-full sm:w-64"
                      aria-label={t('settings.workflow.label', { stage: defaultName })}
                      placeholder={defaultName}
                      maxLength={MAX_LABEL_LENGTH}
                      value={row.label}
                      disabled={!canWrite || !(stage.required || row.enabled)}
                      onChange={(event) => change(stage.key, { label: event.target.value })}
                    />
                  </li>
                );
              })}
            </ul>

            <div className="flex flex-wrap items-center gap-3">
              {canWrite ? (
                <Button type="submit" disabled={save.isPending || setStep.isPending}>
                  {t('common.save')}
                </Button>
              ) : null}
              <Button asChild variant="ghost" size="sm">
                <Link to="/settings/stage-flows">{t('settings.workflow.perGarment')}</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
